import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getUploadUrl } from '../utils/helpers';
import { API_BASE_URL } from '../services/api';
import { getPendingReviewCounts, getUnseenCounts } from '../services/adminService';
import { getStudentNotifications } from '../services/studentService';
import { 
  LayoutDashboard, 
  Video, 
  Film, 
  Calendar, 
  Users, 
  PlayCircle, 
  BookOpen, 
  GraduationCap, 
  FileText, 
  FileQuestion, 
  FileCheck, 
  Layers, 
  UserCheck, 
  CreditCard, 
  DollarSign, 
  Star, 
  Briefcase, 
  ClipboardList, 
  ShieldCheck, 
  Tag, 
  MessageSquare,
  Settings as SettingsIcon, 
  Database, 
  PieChart, 
  User, 
  Building2,
  ChevronDown, 
  ChevronRight 
} from 'lucide-react';

const Sidebar = (props) => {
  const { user, role, logout } = useAuth();
  if ((role === 'admin' || role === 'employee') && !props.isLayout) return null;
  const sidebarRef = useRef(null);
  const location = useLocation();

  const formatBadgeCount = (count) => {
    const num = Number(count || 0);
    if (num <= 0) return null;
    if (num > 99) return "99+";
    return num;
  };

  const renderGroupHeader = (title) => (
    <li className="psb-group-header-label" style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
      textTransform: 'uppercase',
      fontSize: '11px',
      fontWeight: '600',
      letterSpacing: '0.12em',
      color: '#7a8582',
      marginTop: '22px',
      marginBottom: '10px',
      padding: '0 20px',
      gap: '6px',
      cursor: 'default',
      userSelect: 'none',
      border: 'none',
      listStyle: 'none'
    }}>
      <span>{title}</span>
      <div style={{ height: '1px', backgroundColor: 'rgba(13,68,62,0.1)', width: '100%' }} />
    </li>
  );

  useEffect(() => {
    const savedPosition = sessionStorage.getItem('sidebar_scroll_position');
    if (savedPosition && sidebarRef.current) {
      sidebarRef.current.scrollTop = Number(savedPosition);
      const timer = setTimeout(() => {
        if (sidebarRef.current) {
          sidebarRef.current.scrollTop = Number(savedPosition);
        }
      }, 0);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleScroll = (e) => {
    sessionStorage.setItem('sidebar_scroll_position', e.target.scrollTop);
  };

  const [isOpen, setIsOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => localStorage.getItem('sidebar_collapsed') === 'true');

  const toggleCollapse = () => {
    setIsCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('sidebar_collapsed', String(next));
      return next;
    });
  };

  useEffect(() => {
    if (isCollapsed) {
      document.body.classList.add('sidebar-collapsed');
    } else {
      document.body.classList.remove('sidebar-collapsed');
    }
  }, [isCollapsed]);

  useEffect(() => {
    document.body.classList.remove('role-student', 'role-admin', 'role-employee', 'role-demo-student');
    if (role === 'student') {
      document.body.classList.add('role-student');
    } else if (role === 'admin') {
      document.body.classList.add('role-admin');
    } else if (role === 'employee') {
      document.body.classList.add('role-employee');
    } else if (role === 'demo_student') {
      document.body.classList.add('role-demo-student');
    }
    return () => {
      document.body.classList.remove('role-student', 'role-admin', 'role-employee', 'role-demo-student');
    };
  }, [role]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const [counts, setCounts] = useState({
    lecture_reviews: 0,
    question_reviews: 0,
    exam_reviews: 0,
    unseen_leads: 0,
    unseen_enrollments: 0,
    fee_due_count: 0,
    unseen_teacher_applications: 0,
    teacher_reviews: 0,
    profile_updates: 0,
    teacher_applications: 0,
    demo_students_pending: 0,
    demo_videos_pending_review: 0
  });

  const [studentCounts, setStudentCounts] = useState({
    pendingExamCount: 0,
    dueFeeCount: 0,
    newCommonCoursesCount: 0,
    hasNewApprovedLecture: false
  });

  const getLogoUrl = () => {
    const apiBase = import.meta.env.VITE_API_BASE_URL || '/my-lms/backend/api';
    return `${apiBase}/logo.php`;
  };

  useEffect(() => {
    if (role !== 'student') return;

    const fetchStudentCounts = async () => {
      try {
        const data = await getStudentNotifications();
        setStudentCounts({
          pendingExamCount: Number(data?.pendingExamCount ?? 0),
          dueFeeCount: Number(data?.dueFeeCount ?? 0),
          newCommonCoursesCount: Number(data?.newCommonCoursesCount ?? 0),
          hasNewApprovedLecture: !!data?.hasNewApprovedLecture
        });
      } catch (err) {
        console.error("Failed to fetch student notifications count:", err);
        setStudentCounts({ pendingExamCount: 0, dueFeeCount: 0, newCommonCoursesCount: 0, hasNewApprovedLecture: false });
      }
    };

    fetchStudentCounts();
    const interval = setInterval(fetchStudentCounts, 120000);
    window.addEventListener('student-notification-updated', fetchStudentCounts);
    return () => {
      clearInterval(interval);
      window.removeEventListener('student-notification-updated', fetchStudentCounts);
    };
  }, [role]);

  useEffect(() => {
    if (role !== 'admin') return;

    const fetchCounts = async () => {
      try {
        const [reviewData, unseenData] = await Promise.all([
          getPendingReviewCounts(),
          getUnseenCounts()
        ]);
        let newCounts = {
          lecture_reviews: 0, question_reviews: 0, exam_reviews: 0,
          unseen_leads: 0, unseen_enrollments: 0, fee_due_count: 0,
          unseen_teacher_applications: 0, teacher_reviews: 0,
          profile_updates: 0, teacher_applications: 0,
          demo_students_pending: 0, demo_videos_pending_review: 0
        };
        if (reviewData) {
          newCounts.lecture_reviews = Number(reviewData.lecture_reviews || 0);
          newCounts.question_reviews = Number(reviewData.question_reviews || 0);
          newCounts.exam_reviews = Number(reviewData.exam_reviews || 0);
          newCounts.teacher_reviews = Number(reviewData.teacher_reviews || 0);
          newCounts.profile_updates = Number(reviewData.profile_updates || 0);
          newCounts.teacher_applications = Number(reviewData.teacher_applications || 0);
          newCounts.demo_students_pending = Number(reviewData.demo_students_pending || 0);
          newCounts.demo_videos_pending_review = Number(reviewData.demo_videos_pending_review || 0);
        }
        if (unseenData) {
          newCounts.unseen_leads = Number(unseenData.unseen_leads || 0);
          newCounts.unseen_enrollments = Number(unseenData.unseen_enrollments || 0);
          newCounts.fee_due_count = Number(unseenData.fee_due_count || 0);
          newCounts.unseen_teacher_applications = Number(unseenData.unseen_teacher_applications || 0);
        }
        setCounts(newCounts);
      } catch (err) {
        console.error("Failed to fetch pending counts:", err);
      }
    };

    const handleNotificationSeen = (e) => {
      if (e.detail && typeof e.detail.updated_total_unseen_count !== 'undefined') {
        setCounts(prev => ({ ...prev, unseen_enrollments: Number(e.detail.updated_total_unseen_count) }));
      } else {
        fetchCounts();
      }
    };

    fetchCounts();
    const interval = setInterval(fetchCounts, 120000);
    window.addEventListener('review-action-completed', fetchCounts);
    window.addEventListener('notification-seen-completed', handleNotificationSeen);
    return () => {
      clearInterval(interval);
      window.removeEventListener('review-action-completed', fetchCounts);
      window.removeEventListener('notification-seen-completed', handleNotificationSeen);
    };
  }, [role]);

  /* ── Helper to determine active collapsible group ID based on current path ── */
  const getGroupIdForPath = (path) => {
    if (!path) return null;
    
    if (path.startsWith('/admin/demo-courses') ||
        path.startsWith('/admin/demo-batches') ||
        path.startsWith('/admin/demo-students') ||
        path.startsWith('/admin/review-demo-videos') ||
        path.startsWith('/admin/demo-videos')) {
      return 'demo-class-management';
    }
    
    if (path.startsWith('/admin/courses') ||
        path.startsWith('/admin/batches') ||
        path.startsWith('/admin/exams-review') ||
        path.startsWith('/admin/review-questions') ||
        path.startsWith('/admin/lectures') ||
        path.startsWith('/admin/review-batch-lectures') ||
        path.startsWith('/admin/course-modules')) {
      return 'regular-class-management';
    }

    if (path.startsWith('/admin/interested-students') ||
        path.startsWith('/admin/students') ||
        path.startsWith('/admin/fee-due') ||
        path.startsWith('/admin/total-fee-collected') ||
        path.startsWith('/admin/teacher-reviews')) {
      return 'student-management';
    }

    if (path.startsWith('/admin/employees') ||
        path.startsWith('/admin/teacher-applications') ||
        path.startsWith('/admin/teacher-profile-updates') ||
        path.startsWith('/admin/faculty-payroll')) {
      return 'teacher-management';
    }

    if (path.startsWith('/admin/centre-course-enquiries')) {
      return 'centre-management';
    }

    if (path.startsWith('/admin/coupons') ||
        path.startsWith('/admin/community') ||
        path.startsWith('/admin/common-courses') ||
        path.startsWith('/admin/question-categories') ||
        path.startsWith('/admin/question-bank') ||
        path.startsWith('/admin/reports') ||
        path.startsWith('/admin/exam-results') ||
        path.startsWith('/admin/settings') ||
        path.startsWith('/admin/profile')) {
      return 'admin-management';
    }

    return null;
  };

  // State to manage open collapsible group (Accordion behavior: only 1 open at a time)
  const [openGroup, setOpenGroup] = useState(() => getGroupIdForPath(location.pathname));

  // Auto-expand group when location changes
  useEffect(() => {
    const activeGroup = getGroupIdForPath(location.pathname);
    if (activeGroup) {
      setOpenGroup(activeGroup);
    }
  }, [location.pathname]);

  const toggleGroup = (groupId, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setOpenGroup(prev => (prev === groupId ? null : groupId));
  };

  const isRouteActive = (itemPath) => {
    const current = location.pathname;
    if (current === itemPath) return true;
    if (itemPath !== '/admin/dashboard' && current.startsWith(itemPath + '/')) return true;
    return false;
  };

  const getPremiumNavLinkClass = ({ isActive }) => `psb-nav-link${isActive ? ' active' : ''}`;

  const handleClose = () => setIsOpen(false);

  /* ── Toggle drawer from Header hamburger ── */
  useEffect(() => {
    const handleToggle = () => setIsOpen(prev => !prev);
    const handleEsc = (e) => { if (e.key === 'Escape') setIsOpen(false); };
    window.addEventListener('toggle-student-drawer', handleToggle);
    window.addEventListener('keydown', handleEsc);
    return () => {
      window.removeEventListener('toggle-student-drawer', handleToggle);
      window.removeEventListener('keydown', handleEsc);
    };
  }, []);

  /* ── Student avatar ── */
  const avatarSrc = (role === 'student')
    ? `${API_BASE_URL}/logo.php`
    : (user?.profile_picture
      ? getUploadUrl(user.profile_picture, `${API_BASE_URL}/logo.php`)
      : `${API_BASE_URL}/logo.php`);
  const isFallbackAvatar = (role === 'student') ? true : !user?.profile_picture;

  /* ═══════════════════════════════════════════════════════════
     STUDENT NAV LINKS
  ═══════════════════════════════════════════════════════════ */
  const renderStudentLinks = (onClickFn) => (
    <>
      <li>
        <NavLink to="/student/dashboard" className={getPremiumNavLinkClass} onClick={onClickFn}>
          <svg viewBox="0 0 24 24"><path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/></svg>
          <span>Dashboard</span>
          {studentCounts.hasNewApprovedLecture && (
            <span className="psb-badge" title="New lecture available!">📹</span>
          )}
        </NavLink>
      </li>
      <li>
        <NavLink to="/student/community" className={getPremiumNavLinkClass} onClick={onClickFn}>
          <svg viewBox="0 0 24 24"><path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM6 9h12v2H6V9zm8 5H6v-2h8v2zm4-6H6V6h12v2z"/></svg>
          <span>Community</span>
        </NavLink>
      </li>
      <li>
        <NavLink to="/student/common-courses" className={getPremiumNavLinkClass} onClick={onClickFn}>
          <svg viewBox="0 0 24 24"><path d="M12 3L1 9l11 6 9-4.91V17h2V9L12 3zM5.49 12C4.3 12.78 3.5 13.8 3.5 15c0 2.2 3.8 4 8.5 4s8.5-1.8 8.5-4c0-1.2-.8-2.22-1.99-3L12 16.22 5.49 12z"/></svg>
          <span>Common Courses</span>
          {studentCounts.newCommonCoursesCount > 0 && (
            <span className="psb-badge">{studentCounts.newCommonCoursesCount}</span>
          )}
        </NavLink>
      </li>
      <li>
        <NavLink to="/student/fees" className={getPremiumNavLinkClass} onClick={onClickFn}>
          <svg viewBox="0 0 24 24"><path d="M21 18v1c0 1.1-.9 2-2 2H5c-1.11 0-2-.9-2-2V5c0-1.1.89-2 2-2h14c1.1 0 2 .9 2 2v1h-9c-1.11 0-2 .9-2 2v8c0 1.1.89 2 2 2h9zm-9-2h10V8H12v8zm4-2.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/></svg>
          <span>My Fee</span>
          {studentCounts.dueFeeCount > 0 && (
            <span className="psb-badge">{studentCounts.dueFeeCount}</span>
          )}
        </NavLink>
      </li>
      <li>
        <NavLink to="/student/exams" className={getPremiumNavLinkClass} onClick={onClickFn}>
          <svg viewBox="0 0 24 24"><path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/></svg>
          <span>Exams</span>
          {studentCounts.pendingExamCount > 0 && (
            <span className="psb-badge">{studentCounts.pendingExamCount}</span>
          )}
        </NavLink>
      </li>
      <li>
        <NavLink to="/student/certificates" className={getPremiumNavLinkClass} onClick={onClickFn}>
          <svg viewBox="0 0 24 24"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 3c1.93 0 3.5 1.57 3.5 3.5S13.93 13 12 13s-3.5-1.57-3.5-3.5S10.07 6 12 6zm7 13H5v-.23c0-.62.28-1.2.76-1.58C7.47 15.82 9.64 15 12 15s4.53.82 6.24 2.19c.48.38.76.97.76 1.58V19z"/></svg>
          <span>My Certificates</span>
        </NavLink>
      </li>
      <li>
        <NavLink to="/student/profile" className={getPremiumNavLinkClass} onClick={onClickFn}>
          <svg viewBox="0 0 24 24"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>
          <span>My Profile</span>
        </NavLink>
      </li>
    </>
  );

  const renderDemoStudentLinks = (onClickFn) => (
    <>
      <li>
        <NavLink to="/demo/dashboard" className={getPremiumNavLinkClass} onClick={onClickFn}>
          <svg viewBox="0 0 24 24"><path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/></svg>
          <span>Dashboard</span>
        </NavLink>
      </li>
      <li>
        <NavLink to="/demo/classroom" className={getPremiumNavLinkClass} onClick={onClickFn}>
          <svg viewBox="0 0 24 24"><path d="M12 3L1 9l11 6 9-4.91V17h2V9L12 3zM5.49 12C4.3 12.78 3.5 13.8 3.5 15c0 2.2 3.8 4 8.5 4s8.5-1.8 8.5-4c0-1.2-.8-2.22-1.99-3L12 16.22 5.49 12z"/></svg>
          <span>My Course</span>
        </NavLink>
      </li>
      <li>
        <NavLink to="/demo/fees" className={getPremiumNavLinkClass} onClick={onClickFn}>
          <svg viewBox="0 0 24 24"><path d="M21 18v1c0 1.1-.9 2-2 2H5c-1.11 0-2-.9-2-2V5c0-1.1.89-2 2-2h14c1.1 0 2 .9 2 2v1h-9c-1.11 0-2 .9-2 2v8c0 1.1.89 2 2 2h9zm-9-2h10V8H12v8zm4-2.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/></svg>
          <span>My Fee</span>
        </NavLink>
      </li>
      <li>
        <NavLink to="/demo/exams" className={getPremiumNavLinkClass} onClick={onClickFn}>
          <svg viewBox="0 0 24 24"><path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/></svg>
          <span>Exams</span>
        </NavLink>
      </li>
      <li>
        <NavLink to="/demo/certificates" className={getPremiumNavLinkClass} onClick={onClickFn}>
          <svg viewBox="0 0 24 24"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 3c1.93 0 3.5 1.57 3.5 3.5S13.93 13 12 13s-3.5-1.57-3.5-3.5S10.07 6 12 6zm7 13H5v-.23c0-.62.28-1.2.76-1.58C7.47 15.82 9.64 15 12 15s4.53.82 6.24 2.19c.48.38.76.97.76 1.58V19z"/></svg>
          <span>My Certificates</span>
        </NavLink>
      </li>
      <li>
        <NavLink to="/demo/profile" className={getPremiumNavLinkClass} onClick={onClickFn}>
          <svg viewBox="0 0 24 24"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>
          <span>My Profile</span>
        </NavLink>
      </li>
    </>
  );

  /* ═══════════════════════════════════════════════════════════
     ADMIN NAV LINKS — Collapsible Professional Groups
  ═══════════════════════════════════════════════════════════ */
  const renderAdminLinks = (onClickFn) => {
    const isGroupActive = (groupId) => openGroup === groupId;
    const isGroupHasActiveRoute = (groupId) => getGroupIdForPath(location.pathname) === groupId;

    return (
      <>
        {/* 1. Dashboard (Standalone Top-Level) */}
        <li>
          <NavLink 
            to="/admin/dashboard" 
            className={`psb-nav-link${isRouteActive('/admin/dashboard') ? ' active' : ''}`} 
            onClick={onClickFn}
          >
            <LayoutDashboard size={18} />
            <span>Dashboard</span>
          </NavLink>
        </li>

        {/* 2. Demo Class Management */}
        <li className="psb-group-item">
          <button 
            type="button" 
            className={`psb-group-header-btn${isGroupHasActiveRoute('demo-class-management') ? ' is-active-group' : ''}`}
            onClick={(e) => toggleGroup('demo-class-management', e)}
          >
            <div className="psb-group-title-content">
              <Video size={18} />
              <span>Demo Class Management</span>
            </div>
            {isGroupActive('demo-class-management') ? (
              <ChevronDown className="psb-group-chevron" size={18} />
            ) : (
              <ChevronRight className="psb-group-chevron" size={18} />
            )}
          </button>

          {isGroupActive('demo-class-management') && (
            <ul className="psb-group-list">
              <li>
                <NavLink to="/admin/demo-courses" className={`psb-nav-link${isRouteActive('/admin/demo-courses') ? ' active' : ''}`} onClick={onClickFn}>
                  <Film size={16} />
                  <span>Demo Courses</span>
                </NavLink>
              </li>
              <li>
                <NavLink to="/admin/demo-batches" className={`psb-nav-link${isRouteActive('/admin/demo-batches') ? ' active' : ''}`} onClick={onClickFn}>
                  <Calendar size={16} />
                  <span>Demo Batches</span>
                </NavLink>
              </li>
              <li>
                <NavLink to="/admin/demo-students" className={`psb-nav-link${isRouteActive('/admin/demo-students') ? ' active' : ''}`} onClick={onClickFn}>
                  <Users size={16} />
                  <span>Demo Students</span>
                  {formatBadgeCount(counts.demo_students_pending) && (
                    <span className="psb-badge">{formatBadgeCount(counts.demo_students_pending)}</span>
                  )}
                </NavLink>
              </li>
              <li>
                <NavLink to="/admin/review-demo-videos" className={`psb-nav-link${isRouteActive('/admin/review-demo-videos') ? ' active' : ''}`} onClick={onClickFn}>
                  <PlayCircle size={16} />
                  <span>Review Demo Videos</span>
                  {formatBadgeCount(counts.demo_videos_pending_review) && (
                    <span className="psb-badge">{formatBadgeCount(counts.demo_videos_pending_review)}</span>
                  )}
                </NavLink>
              </li>
              <li>
                <NavLink to="/admin/demo-videos" className={`psb-nav-link${isRouteActive('/admin/demo-videos') ? ' active' : ''}`} onClick={onClickFn}>
                  <Video size={16} />
                  <span>Demo Videos</span>
                </NavLink>
              </li>
            </ul>
          )}
        </li>

        {/* 3. Regular Class Management */}
        <li className="psb-group-item">
          <button 
            type="button" 
            className={`psb-group-header-btn${isGroupHasActiveRoute('regular-class-management') ? ' is-active-group' : ''}`}
            onClick={(e) => toggleGroup('regular-class-management', e)}
          >
            <div className="psb-group-title-content">
              <BookOpen size={18} />
              <span>Regular Class Management</span>
            </div>
            {isGroupActive('regular-class-management') ? (
              <ChevronDown className="psb-group-chevron" size={18} />
            ) : (
              <ChevronRight className="psb-group-chevron" size={18} />
            )}
          </button>

          {isGroupActive('regular-class-management') && (
            <ul className="psb-group-list">
              <li>
                <NavLink to="/admin/courses" className={`psb-nav-link${isRouteActive('/admin/courses') ? ' active' : ''}`} onClick={onClickFn}>
                  <GraduationCap size={16} />
                  <span>Courses</span>
                </NavLink>
              </li>
              <li>
                <NavLink to="/admin/batches" className={`psb-nav-link${isRouteActive('/admin/batches') ? ' active' : ''}`} onClick={onClickFn}>
                  <Calendar size={16} />
                  <span>Batches</span>
                </NavLink>
              </li>
              <li>
                <NavLink to="/admin/community" className={`psb-nav-link${isRouteActive('/admin/community') ? ' active' : ''}`} onClick={onClickFn}>
                  <Users size={16} />
                  <span>Community Monitor</span>
                </NavLink>
              </li>
              <li>
                <NavLink to="/admin/exams-review" className={`psb-nav-link${isRouteActive('/admin/exams-review') ? ' active' : ''}`} onClick={onClickFn}>
                  <FileText size={16} />
                  <span>Exams</span>
                  {formatBadgeCount(counts.exam_reviews) && (
                    <span className="psb-badge">{formatBadgeCount(counts.exam_reviews)}</span>
                  )}
                </NavLink>
              </li>
              <li>
                <NavLink to="/admin/review-questions" className={`psb-nav-link${isRouteActive('/admin/review-questions') ? ' active' : ''}`} onClick={onClickFn}>
                  <FileQuestion size={16} />
                  <span>Reviews</span>
                  {formatBadgeCount(counts.question_reviews) && (
                    <span className="psb-badge">{formatBadgeCount(counts.question_reviews)}</span>
                  )}
                </NavLink>
              </li>
              <li>
                <NavLink to="/admin/lectures" className={`psb-nav-link${isRouteActive('/admin/lectures') ? ' active' : ''}`} onClick={onClickFn}>
                  <Video size={16} />
                  <span>Batch Lectures</span>
                </NavLink>
              </li>
              <li>
                <NavLink to="/admin/review-batch-lectures" className={`psb-nav-link${isRouteActive('/admin/review-batch-lectures') ? ' active' : ''}`} onClick={onClickFn}>
                  <FileCheck size={16} />
                  <span>Review Batch Lectures</span>
                  {formatBadgeCount(counts.lecture_reviews) && (
                    <span className="psb-badge">{formatBadgeCount(counts.lecture_reviews)}</span>
                  )}
                </NavLink>
              </li>
              <li>
                <NavLink to="/admin/course-modules" className={`psb-nav-link${isRouteActive('/admin/course-modules') ? ' active' : ''}`} onClick={onClickFn}>
                  <Layers size={16} />
                  <span>Course Modules</span>
                </NavLink>
              </li>
            </ul>
          )}
        </li>

        {/* 4. Student Management */}
        <li className="psb-group-item">
          <button 
            type="button" 
            className={`psb-group-header-btn${isGroupHasActiveRoute('student-management') ? ' is-active-group' : ''}`}
            onClick={(e) => toggleGroup('student-management', e)}
          >
            <div className="psb-group-title-content">
              <Users size={18} />
              <span>Student Management</span>
            </div>
            {isGroupActive('student-management') ? (
              <ChevronDown className="psb-group-chevron" size={18} />
            ) : (
              <ChevronRight className="psb-group-chevron" size={18} />
            )}
          </button>

          {isGroupActive('student-management') && (
            <ul className="psb-group-list">
              <li>
                <NavLink to="/admin/course-enquiries" className={`psb-nav-link${(isRouteActive('/admin/course-enquiries') || isRouteActive('/admin/interested-students')) ? ' active' : ''}`} onClick={onClickFn}>
                  <UserCheck size={16} />
                  <span>Course Enquiries</span>
                  {formatBadgeCount(counts.unseen_leads) && (
                    <span className="psb-badge">{formatBadgeCount(counts.unseen_leads)}</span>
                  )}
                </NavLink>
              </li>
              <li>
                <NavLink to="/admin/students" className={`psb-nav-link${isRouteActive('/admin/students') ? ' active' : ''}`} onClick={onClickFn}>
                  <Users size={16} />
                  <span>Students</span>
                  {formatBadgeCount(counts.unseen_enrollments) && (
                    <span className="psb-badge">{formatBadgeCount(counts.unseen_enrollments)}</span>
                  )}
                </NavLink>
              </li>
              <li>
                <NavLink to="/admin/fee-due" className={`psb-nav-link${isRouteActive('/admin/fee-due') ? ' active' : ''}`} onClick={onClickFn}>
                  <CreditCard size={16} />
                  <span>Student Fees</span>
                  {formatBadgeCount(counts.fee_due_count) && (
                    <span className="psb-badge">{formatBadgeCount(counts.fee_due_count)}</span>
                  )}
                </NavLink>
              </li>
              <li>
                <NavLink to="/admin/total-fee-collected" className={`psb-nav-link${isRouteActive('/admin/total-fee-collected') ? ' active' : ''}`} onClick={onClickFn}>
                  <DollarSign size={16} />
                  <span>Total Fees Collected</span>
                </NavLink>
              </li>
              <li>
                <NavLink to="/admin/teacher-reviews" className={`psb-nav-link${isRouteActive('/admin/teacher-reviews') ? ' active' : ''}`} onClick={onClickFn}>
                  <Star size={16} />
                  <span>Teacher Reviews</span>
                  {formatBadgeCount(counts.teacher_reviews) && (
                    <span className="psb-badge">{formatBadgeCount(counts.teacher_reviews)}</span>
                  )}
                </NavLink>
              </li>
            </ul>
          )}
        </li>

        {/* 5. Teacher Management */}
        <li className="psb-group-item">
          <button 
            type="button" 
            className={`psb-group-header-btn${isGroupHasActiveRoute('teacher-management') ? ' is-active-group' : ''}`}
            onClick={(e) => toggleGroup('teacher-management', e)}
          >
            <div className="psb-group-title-content">
              <Briefcase size={18} />
              <span>Teacher Management</span>
            </div>
            {isGroupActive('teacher-management') ? (
              <ChevronDown className="psb-group-chevron" size={18} />
            ) : (
              <ChevronRight className="psb-group-chevron" size={18} />
            )}
          </button>

          {isGroupActive('teacher-management') && (
            <ul className="psb-group-list">
              <li>
                <NavLink to="/admin/employees" className={`psb-nav-link${isRouteActive('/admin/employees') ? ' active' : ''}`} onClick={onClickFn}>
                  <UserCheck size={16} />
                  <span>Employees</span>
                </NavLink>
              </li>
              <li>
                <NavLink to="/admin/teacher-applications" className={`psb-nav-link${isRouteActive('/admin/teacher-applications') ? ' active' : ''}`} onClick={onClickFn}>
                  <ClipboardList size={16} />
                  <span>Teacher Applications</span>
                  {formatBadgeCount(counts.teacher_applications) && (
                    <span className="psb-badge">{formatBadgeCount(counts.teacher_applications)}</span>
                  )}
                </NavLink>
              </li>
              <li>
                <NavLink to="/admin/teacher-profile-updates" className={`psb-nav-link${isRouteActive('/admin/teacher-profile-updates') ? ' active' : ''}`} onClick={onClickFn}>
                  <FileText size={16} />
                  <span>Profile Updates</span>
                  {formatBadgeCount(counts.profile_updates) && (
                    <span className="psb-badge">{formatBadgeCount(counts.profile_updates)}</span>
                  )}
                </NavLink>
              </li>
              <li>
                <NavLink to="/admin/faculty-payroll" className={`psb-nav-link${isRouteActive('/admin/faculty-payroll') ? ' active' : ''}`} onClick={onClickFn}>
                  <CreditCard size={16} />
                  <span>Faculty Payroll Management</span>
                </NavLink>
              </li>
            </ul>
          )}
        </li>

        {/* 6. Centre Management */}
        <li className="psb-group-item">
          <button 
            type="button" 
            className={`psb-group-header-btn${isGroupHasActiveRoute('centre-management') ? ' is-active-group' : ''}`}
            onClick={(e) => toggleGroup('centre-management', e)}
          >
            <div className="psb-group-title-content">
              <Building2 size={18} />
              <span>Centre Management</span>
            </div>
            {isGroupActive('centre-management') ? (
              <ChevronDown className="psb-group-chevron" size={18} />
            ) : (
              <ChevronRight className="psb-group-chevron" size={18} />
            )}
          </button>

          {isGroupActive('centre-management') && (
            <ul className="psb-group-list">
              <li>
                <NavLink to="/admin/centre-course-enquiries" className={`psb-nav-link${isRouteActive('/admin/centre-course-enquiries') ? ' active' : ''}`} onClick={onClickFn}>
                  <ClipboardList size={16} />
                  <span>Centre Course Enquiries</span>
                </NavLink>
              </li>
            </ul>
          )}
        </li>

        {/* 7. Admin Management */}
        <li className="psb-group-item">
          <button 
            type="button" 
            className={`psb-group-header-btn${isGroupHasActiveRoute('admin-management') ? ' is-active-group' : ''}`}
            onClick={(e) => toggleGroup('admin-management', e)}
          >
            <div className="psb-group-title-content">
              <ShieldCheck size={18} />
              <span>Admin Management</span>
            </div>
            {isGroupActive('admin-management') ? (
              <ChevronDown className="psb-group-chevron" size={18} />
            ) : (
              <ChevronRight className="psb-group-chevron" size={18} />
            )}
          </button>

          {isGroupActive('admin-management') && (
            <ul className="psb-group-list">
              <li>
                <NavLink to="/admin/coupons" className={`psb-nav-link${isRouteActive('/admin/coupons') ? ' active' : ''}`} onClick={onClickFn}>
                  <Tag size={16} />
                  <span>Coupons</span>
                </NavLink>
              </li>
              <li>
                <NavLink to="/admin/community" className={`psb-nav-link${isRouteActive('/admin/community') ? ' active' : ''}`} onClick={onClickFn}>
                  <MessageSquare size={16} />
                  <span>Community</span>
                </NavLink>
              </li>
              <li>
                <NavLink to="/admin/common-courses" className={`psb-nav-link${isRouteActive('/admin/common-courses') ? ' active' : ''}`} onClick={onClickFn}>
                  <BookOpen size={16} />
                  <span>Common Courses</span>
                </NavLink>
              </li>
              <li>
                <NavLink to="/admin/question-categories" className={`psb-nav-link${isRouteActive('/admin/question-categories') ? ' active' : ''}`} onClick={onClickFn}>
                  <SettingsIcon size={16} />
                  <span>Question Settings</span>
                </NavLink>
              </li>
              <li>
                <NavLink to="/admin/question-bank" className={`psb-nav-link${isRouteActive('/admin/question-bank') ? ' active' : ''}`} onClick={onClickFn}>
                  <Database size={16} />
                  <span>Question Bank</span>
                </NavLink>
              </li>
              <li>
                <NavLink to="/admin/reports" className={`psb-nav-link${isRouteActive('/admin/reports') ? ' active' : ''}`} onClick={onClickFn}>
                  <PieChart size={16} />
                  <span>Reports</span>
                </NavLink>
              </li>
              <li>
                <NavLink to="/admin/exam-results" className={`psb-nav-link${isRouteActive('/admin/exam-results') ? ' active' : ''}`} onClick={onClickFn}>
                  <FileText size={16} />
                  <span>Review Question Settings</span>
                </NavLink>
              </li>
              <li>
                <NavLink to="/admin/profile" className={`psb-nav-link${isRouteActive('/admin/profile') ? ' active' : ''}`} onClick={onClickFn}>
                  <User size={16} />
                  <span>My Profile</span>
                </NavLink>
              </li>
            </ul>
          )}
        </li>
      </>
    );
  };

  /* ═══════════════════════════════════════════════════════════
     EMPLOYEE NAV LINKS
  ═══════════════════════════════════════════════════════════ */
  const renderEmployeeLinks = (onClickFn) => (
    <>
      <li>
        <NavLink to="/employee/dashboard" className={getPremiumNavLinkClass} onClick={onClickFn}>
          <svg viewBox="0 0 24 24"><path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z"/></svg>
          <span>Dashboard</span>
        </NavLink>
      </li>
      <li>
        <NavLink to="/employee/my-batches" className={getPremiumNavLinkClass} onClick={onClickFn}>
          <svg viewBox="0 0 24 24"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/></svg>
          <span>My Batches</span>
        </NavLink>
      </li>
      <li>
        <NavLink to="/employee/community" className={getPremiumNavLinkClass} onClick={onClickFn}>
          <svg viewBox="0 0 24 24"><path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM6 9h12v2H6V9zm8 5H6v-2h8v2zm4-6H6V6h12v2z"/></svg>
          <span>Community</span>
        </NavLink>
      </li>

      {renderGroupHeader("Content Submission")}
      <li>
        <NavLink to="/employee/add-lecture" className={getPremiumNavLinkClass} onClick={onClickFn}>
          <svg viewBox="0 0 24 24"><path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4zM14 13h-3v3H9v-3H6v-2h3V8h2v3h3v2z"/></svg>
          <span>Add Daily Lecture</span>
        </NavLink>
      </li>
      <li>
        <NavLink to="/employee/add-question" className={getPremiumNavLinkClass} onClick={onClickFn}>
          <svg viewBox="0 0 24 24"><path d="M19 3h-4.18C14.4 1.84 13.3 1 12 1c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm2 14H7v-2h8v2zm0-4H7v-2h8v2zm0-4H7V7h8v2z"/></svg>
          <span>Add Questions</span>
        </NavLink>
      </li>
      <li>
        <NavLink to="/employee/create-exam" className={getPremiumNavLinkClass} onClick={onClickFn}>
          <svg viewBox="0 0 24 24"><path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/></svg>
          <span>Create Exam Paper</span>
        </NavLink>
      </li>
      <li>
        <NavLink to="/employee/add-demo-video" className={getPremiumNavLinkClass} onClick={onClickFn}>
          <svg viewBox="0 0 24 24"><path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z"/></svg>
          <span>Upload Demo Videos</span>
        </NavLink>
      </li>

      {renderGroupHeader("Teaching Resources")}
      <li>
        <NavLink to="/employee/my-lectures" className={getPremiumNavLinkClass} onClick={onClickFn}>
          <svg viewBox="0 0 24 24"><path d="M12 11.55C9.64 9.35 6.48 8.45 3 8.45v11.5c3.48 0 6.64.9 9 3.1 2.36-2.2 5.52-3.1 9-3.1V8.45c-3.48 0-6.64.9-9 3.1zm0-7.05c-2.36-2.2-5.52-3.1-9-3.1V3c3.48 0 6.64.9 9 3.1 2.36-2.2 5.52-3.1 9-3.1v1.6c-3.48 0-6.64.9-9 3.1z"/></svg>
          <span>Uploaded Lectures</span>
        </NavLink>
      </li>
      <li>
        <NavLink to="/employee/demo-videos" className={getPremiumNavLinkClass} onClick={onClickFn}>
          <svg viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z"/></svg>
          <span>Demo Videos</span>
        </NavLink>
      </li>
      <li>
        <NavLink to="/employee/my-question-bank" className={getPremiumNavLinkClass} onClick={onClickFn}>
          <svg viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 4.02 2 6.5v11C2 20.02 6.48 22 12 22s10-1.98 10-4.5v-11C22 4.02 17.52 2 12 2zm0 3c4.14 0 7 1.15 7 2s-2.86 2-7 2-7-1.15-7-2 2.86-2 7-2zm0 14c-4.14 0-7-1.15-7-2v-2.38c1.55.85 4.14 1.38 7 1.38s5.45-.53 7-1.38V17c0 .85-2.86 2-7 2zm0-5c-4.14 0-7-1.15-7-2v-2.38c1.55.85 4.14 1.38 7 1.38s5.45-.53 7-1.38V12c0 .85-2.86 2-7 2z"/></svg>
          <span>Question Bank</span>
        </NavLink>
      </li>
      <li>
        <NavLink to="/employee/exams" className={getPremiumNavLinkClass} onClick={onClickFn}>
          <svg viewBox="0 0 24 24"><path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/></svg>
          <span>Question Papers</span>
        </NavLink>
      </li>

      {renderGroupHeader("General")}
      <li>
        <NavLink to="/employee/students" className={getPremiumNavLinkClass} onClick={onClickFn}>
          <svg viewBox="0 0 24 24"><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/></svg>
          <span>Students</span>
        </NavLink>
      </li>
      <li>
        <NavLink to="/employee/certificates" className={getPremiumNavLinkClass} onClick={onClickFn}>
          <svg viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
          <span>Certificates</span>
        </NavLink>
      </li>
      <li>
        <NavLink to="/employee/payments" className={getPremiumNavLinkClass} onClick={onClickFn}>
          <svg viewBox="0 0 24 24"><path d="M21 18v1c0 1.1-.9 2-2 2H5c-1.11 0-2-.9-2-2V5c0-1.1.89-2 2-2h14c1.1 0 2 .9 2 2v1h-9c-1.11 0-2 .9-2 2v8c0 1.1.89 2 2 2h9zm-9-2h10V8H12v8zm4-2.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/></svg>
          <span>Payments</span>
        </NavLink>
      </li>
      <li>
        <NavLink to="/employee/profile" className={getPremiumNavLinkClass} onClick={onClickFn}>
          <svg viewBox="0 0 24 24"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>
          <span>Profile</span>
        </NavLink>
      </li>
    </>
  );

  /* ═══════════════════════════════════════════════════════════
     RENDER
  ═══════════════════════════════════════════════════════════ */
  return (
    <>
      {/* ── PREMIUM DESKTOP SIDEBAR ── */}
      <aside 
        ref={sidebarRef}
        onScroll={handleScroll}
        className={`premium-student-sidebar${isCollapsed ? ' collapsed' : ''}`}
      >
        {/* Toggle chevron button */}
        <button className="sidebar-toggle-btn" onClick={toggleCollapse} aria-label="Toggle Sidebar">
          <svg viewBox="0 0 24 24">
            <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
          </svg>
        </button>

        {/* Logo area */}
        <div className="psb-logo-area">
          <img src={getLogoUrl()} className="psb-logo-img" alt="PranaFlow Academy" />
          <div className="psb-logo-text">
            <span className="psb-logo-title">PranaFlow Academy</span>
            <span className="psb-logo-sub">
              {role === 'student' ? 'Student Portal' : role === 'admin' ? 'Admin Portal' : role === 'employee' ? 'Teacher Portal' : 'Demo Portal'}
            </span>
          </div>
        </div>

        {/* Navigation section label */}
        <div className="psb-nav-section-label">Main Menu</div>

        {/* Nav links */}
        <ul className="psb-nav-list">
          {role === 'student' && renderStudentLinks(undefined)}
          {role === 'admin' && renderAdminLinks(undefined)}
          {role === 'employee' && renderEmployeeLinks(undefined)}
          {role === 'demo_student' && renderDemoStudentLinks(undefined)}
        </ul>
      </aside>

      {/* ── MOBILE DRAWER OVERLAY ── */}
      {isOpen && (
        <div
          className="student-drawer-overlay"
          onClick={handleClose}
          style={{
            position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.45)', backdropFilter: 'blur(2px)', zIndex: 999
          }}
        />
      )}

      {/* ── MOBILE DRAWER ── Premium white layout for all roles ── */}
      <div
        className={`student-mobile-drawer${isOpen ? ' open' : ''}`}
        style={{
          position: 'fixed', top: 0, left: 0, height: '100%',
          width: '290px',
          backgroundColor: '#ffffff',
          boxShadow: '6px 0 32px rgba(0,0,0,0.2)',
          zIndex: 1000, display: 'flex', flexDirection: 'column',
          transform: isOpen ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          boxSizing: 'border-box'
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center',
          padding: '20px 20px 16px', borderBottom: '1px solid #EEF4F2'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <img src={getLogoUrl()} style={{ width: '32px', height: '32px', objectFit: 'contain' }} alt="Logo" />
            <div>
              <div style={{ fontSize: '0.85rem', fontWeight: 800, color: '#0D443E', lineHeight: 1.2 }}>PranaFlow Academy</div>
              <div style={{ fontSize: '0.65rem', fontWeight: 600, color: '#7A8582', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                {role === 'student' ? 'Student Portal' : role === 'admin' ? 'Admin Portal' : role === 'employee' ? 'Teacher Portal' : 'Demo Portal'}
              </div>
            </div>
          </div>
        </div>

        {/* Links */}
        <div style={{ flexGrow: 1, overflowY: 'auto', padding: '8px 8px' }}>
          <div style={{
            fontSize: '0.62rem', fontWeight: 800, color: '#7A8582',
            textTransform: 'uppercase', letterSpacing: '1.2px',
            padding: '10px 8px 4px'
          }}>Main Menu</div>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '3px' }}>
            {role === 'student' && renderStudentLinks(handleClose)}
            {role === 'admin' && renderAdminLinks(handleClose)}
            {role === 'employee' && renderEmployeeLinks(handleClose)}
            {role === 'demo_student' && renderDemoStudentLinks(handleClose)}
          </ul>
        </div>

        {/* Mini profile */}
        <div className="psb-mini-profile" style={{ margin: '0 12px 12px' }}>
          <img
            src={avatarSrc}
            className={`psb-mini-profile-avatar${isFallbackAvatar ? ' fallback' : ''}`}
            alt="Profile"
          />
          <div className="psb-mini-profile-info">
            <div className="psb-mini-profile-name">{user?.name || (role === 'admin' ? 'Admin' : 'Teacher')}</div>
            <div className="psb-mini-profile-email">{user?.email || ''}</div>
          </div>
        </div>

        {/* Logout */}
        <button
          className="psb-logout-btn"
          onClick={() => { handleClose(); logout(); }}
          style={{ margin: '0 12px 20px' }}
        >
          <svg viewBox="0 0 24 24"><path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5-5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"/></svg>
          Logout
        </button>
      </div>
    </>
  );
};

export default Sidebar;
